import { useCallback, useEffect, useRef, useState } from "react";
import { saveEmissionFactors, saveKpis } from "../kpiGridApi";
import { kpiIdsByTitle } from "./helpers";

const CONFIRMATION_MS = 3200;


const messageOf = (resOrError, fallback) =>
  resOrError?.data?.error || resOrError?.message || fallback;

/**
 * @param {number} rowCount
 * @param {number} factorCount KPIs whose factor list changed
 */
const summarise = (rowCount, factorCount) => {
  const parts = [];
  if (rowCount) parts.push(`${rowCount} row${rowCount === 1 ? "" : "s"}`);
  if (factorCount) parts.push(`factors on ${factorCount} KPI${factorCount === 1 ? "" : "s"}`);
  return `Saved ${parts.join(" and ")}`;
};

/**
 * The save lifecycle for the whole grid: one request, one outcome message.
 *
 * Every changed and new row goes in a single POST — never a loop of per-row
 * calls — so a partial failure is the backend's transaction to roll back, not
 * a half-written grid for the user to reconcile.
 *
 * Emission factors live in their own table and go in a second bulk call, which
 * is itself transactional. The KPI save runs first because it is the one that
 * can create rows, and a factor needs a `kpi_id` that exists — the response
 * carries each row's `question_id` and `title`, so a brand-new KPI's factors
 * are attached in the same save rather than waiting for a second visit.
 *
 * If the factor call fails the KPI edits are already committed, so the message
 * says which half landed rather than implying the whole save was lost.
 *
 * On success the caller's `onSaved` clears the drafts and refreshes in the
 * background, so rows keep scroll position and the newly saved keys appear.
 *
 * @param {{ onSaved: () => Promise<void>|void }} args
 */
export function useSaveRows({ onSaved }) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [savedMessage, setSavedMessage] = useState("");
  const timerRef = useRef(null);

  useEffect(
    () => () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    },
    [],
  );

  const announce = useCallback((message) => {
    setSavedMessage(message);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setSavedMessage(""), CONFIRMATION_MS);
  }, []);

  /**
   * @param {object} payload from helpers.buildSavePayload
   * @param {(resolveKpiId: (row: object) => number|null) => { items: object[], unresolved: string[], conflicting: string[] }} buildFactors
   * @returns {Promise<boolean>} whether the grid should consider itself clean
   */
  const save = useCallback(
    async (payload, buildFactors) => {
      const rowCount = payload.questions.length;
      const upfront = buildFactors(() => null);
      if (!rowCount && !upfront.items.length) return true;
      if (upfront.conflicting.length) {
        setError(
          `Emission factors are shared by every year of a KPI, but the years differ for: ${upfront.conflicting.join(", ")} — nothing was written`,
        );
        return false;
      }

      setSaving(true);
      setError(null);
      let rowsWritten = false;
      let savedIdsByTitle = new Map();
      try {
        if (rowCount) {
          const res = await saveKpis(payload);
          if (res?.isSuccess === false) {
            setError(messageOf(res, "The save was rejected — nothing was written"));
            return false;
          }
          savedIdsByTitle = kpiIdsByTitle(res?.data?.data);
          rowsWritten = true;
        }

        const { items: factorItems, unresolved, conflicting } = buildFactors((row) =>
          savedIdsByTitle.get(String(row.title).trim().toLowerCase()) ?? null,
        );

        if (conflicting.length) {
          setError(
            `Saved the KPI rows, but their years carry different emission factors for: ${conflicting.join(", ")}`,
          );
          await onSaved();
          return false;
        }

        if (unresolved.length) {
          setError(
            `Saved the KPI rows, but could not attach factors to: ${unresolved.join(", ")}`,
          );
          await onSaved();
          return false;
        }

        if (factorItems.length) {
          const res = await saveEmissionFactors(factorItems);
          if (res?.isSuccess === false) {
            const reason = messageOf(res, "The emission factors were rejected");
            setError(
              rowsWritten
                ? `${reason} — the KPI edits saved, the factors did not`
                : `${reason} — nothing was written`,
            );
            return false;
          }
        }

        await onSaved();
        announce(summarise(rowCount, factorItems.length));
        return true;
      } catch (e) {
        const reason = messageOf(e, "The save could not be sent");
        setError(
          rowsWritten
            ? `${reason} — the KPI edits saved, the factors did not`
            : `${reason} — nothing was written`,
        );
        return false;
      } finally {
        setSaving(false);
      }
    },
    [onSaved, announce],
  );

  const dismissError = useCallback(() => setError(null), []);

  return { saving, error, savedMessage, save, dismissError };
}
